import { SourceType } from './sourceTypes';

/** CSL name: personal (family/given) or organization (literal). */
export type CslName = {
  family?: string;
  given?: string;
  literal?: string;
};

export type NameDraft = {
  family: string;
  given: string;
  literal: string;
  isOrganization: boolean;
  /** Set when V2 lookup split a display name; omitted from CSL-JSON. */
  needsVerification?: boolean;
};

export type DateDraft = {
  year: string;
  month: string;
  day: string;
};

/**
 * Form state whose keys are CSL-JSON properties (plan §5). Dates are split
 * in the UI into year/month/day and assembled into `issued` / `accessed`
 * `date-parts` on serialize. V2 lookup pre-fill can write this shape.
 */
export type FormState = {
  type: SourceType;
  author: NameDraft[];
  editor: NameDraft[];
  title: string;
  'container-title': string;
  volume: string;
  issue: string;
  page: string;
  publisher: string;
  'publisher-place': string;
  edition: string;
  issued: DateDraft;
  accessed: DateDraft;
  URL: string;
  DOI: string;
};

export function emptyName(): NameDraft {
  return { family: '', given: '', literal: '', isOrganization: false };
}

export function emptyDate(): DateDraft {
  return { year: '', month: '', day: '' };
}

export function emptyForm(type: SourceType = 'article-journal'): FormState {
  return {
    type,
    author: [emptyName()],
    editor: [emptyName()],
    title: '',
    'container-title': '',
    volume: '',
    issue: '',
    page: '',
    publisher: '',
    'publisher-place': '',
    edition: '',
    issued: emptyDate(),
    accessed: emptyDate(),
    URL: '',
    DOI: '',
  };
}

function trim(value: string): string {
  return value.trim();
}

function toCslName(draft: NameDraft): CslName | null {
  if (draft.isOrganization) {
    const literal = trim(draft.literal);
    return literal === '' ? null : { literal };
  }
  const family = trim(draft.family);
  const given = trim(draft.given);
  if (family === '' && given === '') return null;
  const name: CslName = {};
  if (family !== '') name.family = family;
  if (given !== '') name.given = given;
  return name;
}

function toDateParts(
  draft: DateDraft,
): { 'date-parts': number[][] } | undefined {
  const year = Number.parseInt(draft.year, 10);
  if (!Number.isFinite(year)) return undefined;
  const parts: number[] = [year];
  const month = Number.parseInt(draft.month, 10);
  if (Number.isFinite(month) && month >= 1 && month <= 12) {
    parts.push(month);
    const day = Number.parseInt(draft.day, 10);
    if (Number.isFinite(day) && day >= 1 && day <= 31) {
      parts.push(day);
    }
  }
  return { 'date-parts': [parts] };
}

function compactNames(drafts: NameDraft[]): CslName[] | undefined {
  const names = drafts
    .map(toCslName)
    .filter((name): name is CslName => name !== null);
  return names.length > 0 ? names : undefined;
}

/** CSL-JSON item for the Pass 1 engine. Empty fields are omitted. */
export function toCslJson(form: FormState): Record<string, unknown> {
  const item: Record<string, unknown> = { type: form.type };
  const author = compactNames(form.author);
  if (author) item.author = author;
  const editor = compactNames(form.editor);
  if (editor) item.editor = editor;

  const scalars: Array<keyof FormState> = [
    'title',
    'container-title',
    'volume',
    'issue',
    'page',
    'publisher',
    'publisher-place',
    'edition',
    'URL',
    'DOI',
  ];
  scalars.forEach((key) => {
    const value = trim(form[key] as string);
    if (value !== '') item[key] = value;
  });

  const issued = toDateParts(form.issued);
  if (issued) item.issued = issued;
  const accessed = toDateParts(form.accessed);
  if (accessed) item.accessed = accessed;

  return item;
}

export function hasCitableContent(item: Record<string, unknown>): boolean {
  const authors = item.author as CslName[] | undefined;
  return (
    (typeof item.title === 'string' && item.title !== '') ||
    (typeof item.URL === 'string' && item.URL !== '') ||
    (authors !== undefined && authors.length > 0)
  );
}
